<script setup lang="ts">
    import InfoIcon from "@/assets/info_dark.svg";
    import KButton from "@/components/Button.vue";
    import KButtonGroup from "@/components/ButtonGroup.vue";
    import ModalContent from "@/components/generic/ModalContent.vue";
    import { Sparr } from "@/tabs/Receptforskrivning/types";
    import { toRefs } from "vue";

    type Sparrar = {
        [key in "BEHANDLINGSORSAK" | "SEKRETESS_VH"]: {
            title: string;
            titlePapper?: string;
            heading: string;
            headingPapper?: string;
            body: string;
            bodyPapper?: string;
            nll: string;
            nllPapper?: string;
        };
    };

    const props = defineProps<{
        sparrId: Sparr["id"] | null;
        sparrType: keyof Sparrar | null;
        isPapper?: boolean;
    }>();

    const { sparrId, sparrType, isPapper } = toRefs(props);

    const sparrar: Sparrar = {
        BEHANDLINGSORSAK: {
            title: "Häv spärr behandlingsorsak",
            heading: "Häv spärr av behandlingsorsak för patient",
            body: `
                        Du är på väg att häva spärren av behandlingsorsaken. Den blir då synlig för patienten i
                        Läkemedelskollen och apotekens e-handelstjänster.
                    `,
            nll: `
                        Som förskrivare av läkemedel begär jag att E-hälsomyndigheten ska häva en spärr av
                        behandlingsorsak på en förskrivning för direktåtkomst för patienten. Jag har efter
                        menprövning enligt offentlighets- och sekretesslagen (25 kap. 6 §) eller patientsäkerhetslagen
                        (6 kap. 12 § andra stycket) bedömt att uppgiften kan lämnas ut till patienten.
                    `,
        },
        SEKRETESS_VH: {
            title: "Häv spärr vårdnadshavare",
            titlePapper: "Häv spärr uttag pappersrecept",
            heading: "Häv spärr vårdnadshavare",
            headingPapper: "Häv spärr uttag pappersrecept",
            bodyPapper: `
                    Du är på väg att ta bort spärren för barnets vårdnadshavare. Då kan vårdnadshavaren se
                    samtliga uttag gjorda på pappersrecept i Läkemedelskollen och apotekens e-handelstjänster.
                `,
            body: `
                    Du är på väg att ta bort spärren för barnets vårdnadshavare. Då kan vårdnadshavaren se
                    förskrivningen i Läkemedelskollen och apotekens e-handelstjänster.
                `,
            nllPapper: `
                Som förskrivare av läkemedel begär jag att E-hälsomyndigheten ska häva en spärr av samtliga uttag
                gjorda på pappersrecept för direktåtkomst för barnets vårdnadshavare. Jag har efter menprövning
                enligt offentlighets- och sekretesslagen (12 kap. 3 §) eller patientsäkerhetslagen (6 kap. 12 §)
                bedömt att uppgiften kan lämnas ut till vårdnadshavaren.
            `,
            nll: `
                    Som förskrivare av läkemedel begär jag att E-hälsomyndigheten ska häva en spärrad förskrivning
                    och dess uttag för direktåtkomst för barnets vårdnadshavare. Jag har efter menprövning enligt
                    offentlighets- och sekretesslagen (12 kap. 3 §) eller patientsäkerhetslagen (6 kap. 12 §)
                    bedömt att uppgiften kan lämnas ut till vårdnadshavaren.
                `,
        },
    };

    const emit = defineEmits<{
        (event: "submit", sparrId: Sparr["id"]): void;
        (event: "cancel"): void;
    }>();

    const onSubmit = () => {
        emit("submit", sparrId.value as Sparr["id"]);
    };

    const onCancel = () => {
        emit("cancel");
    };
</script>

<template>
    <portal v-if="sparrType && sparrId" to="modal">
        <ModalContent :title="isPapper ? sparrar[sparrType].titlePapper : sparrar[sparrType].title">
            <h4>{{ isPapper ? sparrar[sparrType].headingPapper : sparrar[sparrType].heading }}</h4>
            <div class="body">
                <img :src="InfoIcon" />
                <p>
                    {{ isPapper ? sparrar[sparrType].bodyPapper : sparrar[sparrType].body }}
                </p>
            </div>
            <div class="nll">
                <div class="heading">Informationstext från NLL:</div>
                <p>{{ isPapper ? sparrar[sparrType].nllPapper : sparrar[sparrType].nll }}</p>
            </div>
            <KButtonGroup>
                <KButton variant="alert" @click="onSubmit">Häv spärr</KButton>
                <KButton variant="contour" size="secondary" @click="onCancel">Avbryt</KButton>
            </KButtonGroup>
        </ModalContent>
    </portal>
</template>

<style lang="scss" scoped>
    @import "@/style/_variables";

    .body {
        display: flex;
        align-items: flex-start;
        margin-bottom: 20px;

        img {
            width: 16px;
            height: 16px;
            margin-right: 10px;
        }

        p {
            margin: 0;
        }
    }

    .nll {
        margin-top: 20px;

        .heading {
            font-weight: 700;
            font-family: "Roboto Medium", sans-serif;
        }
    }
</style>
